import {
    ActiveFilterComponentWrapper,
    AddFilterButtonWrapper,
    ClearFilterWrapper,
    DropdownContainer,
    DropdownIconWrapper,
    DropdownRemoveFiltersNote,
    StyledTab,
    TabListWrapper,
    TrashIconWrapper,
} from "app/pages/segments/redesigned-wizard/segment-wizard/segment-rules-section/segmentRulesStyles";
import { SegmentFilterListItem } from "app/pages/segments/redesigned-wizard/segment-wizard/segment-rules-section/SegmentFilterListItem";
import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { i18nFilter } from "app/filters/i18nFilter";
import { RuleSelectedValuesRow } from "app/pages/segments/redesigned-wizard/segment-wizard/segment-rules-section/RuleSelectedValuesRow";
import type { FC } from "react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { IPagesFilter, IRule, IRulesFilterRow } from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import { RuleTypes } from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import { useSegmentWizardContext } from "app/pages/segments/redesigned-wizard/segment-wizard/SegmentWizardContextProvider";
import { noop, isEqual } from "lodash";
import { TabList, TabPanel, Tabs } from "@similarweb/ui-components";
import { Dropdown, DropdownButton } from "@similarweb/ui-components";
import { removeItems } from "app/pages/segments/redesigned-wizard/segment-wizard/segmentWizardHelpers";
import useDeactivateOnOutsideClick from "app/pages/sales-intelligence/hooks/useDeactivateOnOutsideClick";
import { ClickOutsideWrapper } from "app/custom-hooks/useClickOutside";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";
import { FILTERS_OBJECT } from "app/pages/segments/redesigned-wizard/segment-wizard/constants";
import { doesAtLeastOneRuleExist } from "app/pages/segments/redesigned-wizard/segment-wizard/segmentWizardHelpers";
import { allTrackers } from "app/services/track/track";

export const RuleFilterRow: FC<IRulesFilterRow> = (props) => {
    const {
        isMidTierUser,
        onCloseRule,
        domain,
        getUrlsPreview,
        hideUrlsPreview,
        hasExistingUrlsCompoundStringNoRobots,
        updateWordPredictionsIfNeeded,
        ruleIndex,
        filterType,
        ruleType,
        rule,
        nextRule,
        hasAtLeastOneRule,
        sortedFiltersArray,
        isMidtierUserAndOneRuleCreated,
        shouldDisplayRuleTitle,
        resetActiveIndex,
    } = props;
    const { wordPredictionsLoading, segmentsReady } = useSegmentWizardContext();
    const GREY_BACKGROUND_FRAME_SIZE = 8;
    const TOTAL_TRASH_ICON_SIZE = 48;
    const MAX_CHIPS_IN_ROW = 1;
    const DROPDOWN_CONTAINER_CLASS = "DropdownContent-container";
    const dropdownRef = useRef(null);
    const dropdownContainerRef = useRef(null);
    const selectedFilterRef = useRef(null);
    const [selectedFilter, setSelectedFilter] = useState<IPagesFilter>();
    const [activeFilterComponent, setActiveFilterComponent] = useState(null);
    const [dropdownContainerWidth, setDropdownContainerWidth] = useState<number>(null);
    const [selectedFilterWidth, setSelectedFilterWidth] = useState<number>(null);
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [actualCurrentIndex, setActualCurrentIndex] = useState<number>(0);
    const [actualCurrentRule, setActualCurrentRule] = useState<IRule>(null);
    const [isOpenDropdown, setIsOpenDropdown] = useState(false);

    const isNextRuleNotEmpty = useMemo(() => doesAtLeastOneRuleExist(nextRule), [nextRule]);

    const isTabsComponent = useMemo(
        () => hasAtLeastOneRule && !isNextRuleNotEmpty,
        [isNextRuleNotEmpty, hasAtLeastOneRule],
    );

    const isORModeTab = useMemo(() => actualCurrentIndex === ruleIndex, [actualCurrentIndex, ruleIndex]);

    const clearFilterSelection = () => {
        setSelectedFilter(null);
        setActiveFilterComponent(null);
    };

    const selectFilterComponentByType = (filterType) => {
        const filterIndex = sortedFiltersArray.findIndex((item) => item.filterKey === filterType);
        const selectedFilter = tabsMap[filterIndex];
        setSelectedFilter(selectedFilter);
        return filterIndex;
    };

    const updateWidth = () => {
        const { width: dropdownWidth } = dropdownContainerRef?.current?.getBoundingClientRect() || 0;
        const { width: selectedFilterComponentWidth } = selectedFilterRef?.current?.getBoundingClientRect() || 0;
        setDropdownContainerWidth(dropdownWidth);
        setSelectedFilterWidth(selectedFilterComponentWidth);
    };

    useEffect(() => {
        window.addEventListener("resize", updateWidth, { capture: true });
        setActualCurrentIndex(ruleIndex);
        return () => window.removeEventListener("resize", updateWidth, { capture: true });
    }, []);

    useEffect(
        () => updateWidth(),
        [selectedFilterRef?.current?.getBoundingClientRect(), dropdownContainerRef?.current?.getBoundingClientRect()],
    );

    const rulesSelectedValuesMaxWidth = useMemo(() => {
        return dropdownContainerWidth - selectedFilterWidth - GREY_BACKGROUND_FRAME_SIZE * 2 - TOTAL_TRASH_ICON_SIZE;
    }, [dropdownContainerWidth, selectedFilterWidth]);

    useEffect(() => {
        if (!hasAtLeastOneRule || !filterType) {
            clearFilterSelection();
        } else {
            if (rule?.[filterType]?.length > 0) {
                selectFilterComponentByType(filterType);
            }
        }
        setActualCurrentRule(rule);
    }, [rule, filterType]);

    const resetSelectedFilterAndActualCurrent = useCallback(() => {
        setSelectedFilter(null);
        setActualCurrentIndex(ruleIndex);
        setActualCurrentRule(rule);
    }, [ruleIndex, rule]);

    const onApply = useCallback(
        (currentRule, filterType, selectedTab) => {
            onCloseRule(currentRule, actualCurrentIndex, filterType, selectedTab);
            setActiveFilterComponent(null);
            if (!isEqual(currentRule, rule)) {
                // This is handling edge case when Add filter was selected and applied
                // It resets the data for next add filter selection of the same group
                resetSelectedFilterAndActualCurrent();
            }
        },
        [actualCurrentIndex, onCloseRule, resetSelectedFilterAndActualCurrent, rule],
    );

    const onFilterSelection = useCallback(
        async (selectedValue) => {
            if (isMidTierUser && selectedValue !== FILTERS_OBJECT.FOLDERS.filterKey) {
                SegmentsUtils.openMidTierUserUpsellModal();
                return;
            }
            await updateWordPredictionsIfNeeded(actualCurrentIndex, ruleType);
            const filterIndex = selectFilterComponentByType(selectedValue);
            setActiveFilterComponent(sortedFiltersArray[filterIndex]);
            dropdownRef.current.closePopup();
            allTrackers.dispatchTrackEvent(
                "Drop Down",
                "click",
                `choose filter/${selectedValue || ""}/${rule.type === RuleTypes.include ? "include" : "exclude"}/${selectedTab === 1 ? "AND" : "OR"}`,
            );
        },
        [rule, actualCurrentIndex, updateWordPredictionsIfNeeded],
    );
    const tabsMap = [
        {
            isTabsComponent,
            isLocked: false,
            name: FILTERS_OBJECT.FOLDERS.filterKey,
            iconName: "open-folder" as IconName,
            textKey: "segmentsWizard.addFilter.dropdown.folders.label",
            tooltipKey: hideUrlsPreview
                ? "segmentsWizard.addFilter.dropdown.folders.tooltip.disabled"
                : "segmentsWizard.addFilter.dropdown.folders.tooltip",
            tooltipTextI18nProp: hideUrlsPreview ? domain : undefined,
            onClick: () => onFilterSelection(FILTERS_OBJECT.FOLDERS.filterKey),
        },
        {
            isTabsComponent,
            isLocked: isMidTierUser,
            name: FILTERS_OBJECT.EXACT.filterKey,
            iconName: "search-keywords" as IconName,
            textKey: "segmentsWizard.addFilter.dropdown.anyWords.label",
            tooltipKey: "segmentsWizard.addFilter.dropdown.anyWords.tooltip",
            onClick: () => onFilterSelection(FILTERS_OBJECT.EXACT.filterKey),
        },
        {
            isTabsComponent,
            isLocked: isMidTierUser,
            name: FILTERS_OBJECT.WORDS.filterKey,
            iconName: "search-keywords" as IconName,
            textKey: "segmentsWizard.addFilter.dropdown.exactWords.label",
            tooltipKey: "segmentsWizard.addFilter.dropdown.exactWords.tooltip",
            onClick: () => onFilterSelection(FILTERS_OBJECT.WORDS.filterKey),
        },
        {
            isTabsComponent,
            isLocked: isMidTierUser,
            name: FILTERS_OBJECT.EXACT_URLS.filterKey,
            iconName: "globe" as IconName,
            textKey: "segmentsWizard.addFilter.dropdown.exactUrls.label",
            tooltipKey: "segmentsWizard.addFilter.dropdown.exacturl.tooltip",
            onClick: () => onFilterSelection(FILTERS_OBJECT.EXACT_URLS.filterKey),
        },
    ];
    const tabComponents = [
        ...tabsMap.map(
            ({ isTabsComponent, name, iconName, textKey, onClick, tooltipKey, tooltipTextI18nProp, isLocked }) => {
                const componentKey = `${actualCurrentIndex}_${ruleType}_${name}`;
                const isRuleTypeAlreadyDefined = rule?.[name]?.length > 0;
                if (isRuleTypeAlreadyDefined && isORModeTab) {
                    return null;
                }
                return (
                    <SegmentFilterListItem
                        isLocked={isLocked}
                        tooltipTextI18nProp={tooltipTextI18nProp}
                        filterKey={name}
                        hideUrlsPreview={hideUrlsPreview}
                        key={componentKey}
                        isTabsComponent={isTabsComponent}
                        childKey={componentKey}
                        iconName={iconName}
                        textKey={textKey}
                        onClick={onClick}
                        tooltipKey={tooltipKey}
                    />
                );
            },
        ),
    ];

    const filterComponentProps = useMemo(
        () => ({
            rule: actualCurrentRule,
            isLoadingData: wordPredictionsLoading,
            onApply,
            selectedSite: domain,
            getUrlsPreview,
            hideUrlsPreview,
            hasExistingUrlsCompoundStringNoRobots,
            isMidTierUser,
            ruleIndex,
            filterType,
            selectedTab,
        }),
        [
            actualCurrentRule,
            actualCurrentIndex,
            wordPredictionsLoading,
            domain,
            getUrlsPreview,
            hideUrlsPreview,
            hasExistingUrlsCompoundStringNoRobots,
            isMidTierUser,
            filterType,
            selectedTab,
        ],
    );

    // Currently this is not in use, as it's impossible to delete a value from RuleSelectedValuesRow,
    // unless it's in Filter edit mode, and the it's the component from inside the filter component
    // const onRemoveRule = useCallback(onRemoveItems.bind(null, filterType), [
    //     onRemoveItems,
    //     filterType,
    // ]);

    const onRuleDeleteClicked = (ruleToDelete: string[]) => {
        const updatedRule = removeItems(filterType, ruleToDelete, rule);
        onApply(updatedRule, filterType, selectedTab);
        clearFilterSelection();
    };

    const onEditRuleDeleteClicked = (ruleToDelete: string[]) => {
        if (ruleIndex === actualCurrentIndex) {
            onRuleDeleteClicked(ruleToDelete);
        } else {
            setActualCurrentIndex(ruleIndex);
            setActualCurrentRule(rule);
            clearFilterSelection();
        }
    };
    const onTabSelected = (index: number) => {
        if (index !== selectedTab) {
            setSelectedTab(index);
            const effectiveRuleIndex = index === 0 ? ruleIndex : ruleIndex + 1;
            const effectiveRule = index === 0 ? rule : nextRule;
            setActualCurrentIndex(effectiveRuleIndex);
            setActualCurrentRule(effectiveRule);
        }
    };

    const REMOVE_FILTER_NOTE = (
        <DropdownRemoveFiltersNote key="note">
            {i18nFilter()("segment.wizard.new.dropdown.remove.filter.note")}
        </DropdownRemoveFiltersNote>
    );

    const TABS_COMPONENT = (
        <Tabs
            className="ListItemsTabs"
            onSelect={onTabSelected}
        >
            <TabListWrapper>
                <TabList>
                    <StyledTab>{i18nFilter()("segmentsWizard.addFilters.or.label")}</StyledTab>
                    <StyledTab>{i18nFilter()("segmentsWizard.addFilters.and.label")}</StyledTab>
                </TabList>
            </TabListWrapper>
            <TabPanel>{[REMOVE_FILTER_NOTE, ...tabComponents]}</TabPanel>
            <TabPanel>{tabComponents}</TabPanel>
        </Tabs>
    );

    const onClickOutsideDropdown = useCallback(() => {
        setIsOpenDropdown(false);
        setSelectedTab(0);
    }, []);

    const onDropdownClick = useCallback(() => {
        if (isMidtierUserAndOneRuleCreated) {
            SegmentsUtils.openMidTierUserUpsellModal();
        } else {
            setIsOpenDropdown(true);
            //Resetting values, to avoid errors due to clickOutside behaviour
            setActualCurrentIndex(ruleIndex);
            setActualCurrentRule(rule);
        }
    }, [isMidtierUserAndOneRuleCreated, ruleIndex, rule]);

    const onClickOutsideActiveComponent = useCallback(() => {
        setActiveFilterComponent(null);
        resetActiveIndex();
        if (!filterType) {
            resetSelectedFilterAndActualCurrent();
        }
    }, [filterType, resetSelectedFilterAndActualCurrent, resetActiveIndex]);

    const dropdownButtonIconName = isMidtierUserAndOneRuleCreated ? "locked" : "add";

    useDeactivateOnOutsideClick(
        `${DROPDOWN_CONTAINER_CLASS}-${ruleType}-${ruleIndex}-${filterType}`,
        isOpenDropdown,
        onClickOutsideDropdown,
    );

    return (
        <>
            <DropdownContainer
                isSelectedValue={!!selectedFilter}
                ref={dropdownContainerRef}
                displayTopMargin={shouldDisplayRuleTitle}
            >
                <Dropdown
                    dropdownPopupPlacement={"ontop-left"}
                    cssClassContainer={`${DROPDOWN_CONTAINER_CLASS} ${DROPDOWN_CONTAINER_CLASS}-padding ${DROPDOWN_CONTAINER_CLASS}-${ruleType}-${ruleIndex}-${filterType}`}
                    ref={dropdownRef}
                    buttonWidth={"auto"}
                    width={320}
                    disabled={!segmentsReady || !!selectedFilter || isMidtierUserAndOneRuleCreated}
                >
                    {[
                        <AddFilterButtonWrapper
                            onClick={onDropdownClick}
                            key={`${actualCurrentIndex}_${ruleType}_${filterType}_AddFilterButton`}
                            isSelectedValue={!!selectedFilter}
                            data-automation-is-selectable={!selectedFilter}
                        >
                            <span ref={selectedFilterRef}>
                                <DropdownButton
                                    minWidth={144}
                                    disabled={!segmentsReady}
                                    hideArrow={!!selectedFilter}
                                >
                                    {selectedFilter && (
                                        <SegmentFilterListItem
                                            isSelectedValue={true}
                                            isTabsComponent={selectedFilter.isTabsComponent}
                                            iconName={selectedFilter.iconName}
                                            textKey={selectedFilter.textKey}
                                            onClick={selectedFilter.onClick}
                                            tooltipKey={selectedFilter.tooltipKey}
                                        />
                                    )}
                                    {!selectedFilter && (
                                        <>
                                            <DropdownIconWrapper isLocked={isMidtierUserAndOneRuleCreated}>
                                                <SWReactIcons
                                                    size="sm"
                                                    iconName={dropdownButtonIconName}
                                                />
                                            </DropdownIconWrapper>
                                            <span>{i18nFilter()("segmentsWizard.addFilter.dropdown.label")}</span>
                                        </>
                                    )}
                                </DropdownButton>
                            </span>
                        </AddFilterButtonWrapper>,
                        ...[
                            isTabsComponent ? (
                                TABS_COMPONENT
                            ) : (
                                <React.Fragment key="filterTabsWrapper">{isNextRuleNotEmpty && REMOVE_FILTER_NOTE}
                                {...tabComponents}</React.Fragment>
                            ),
                        ],
                    ]}
                </Dropdown>
                {activeFilterComponent && (
                    <ClearFilterWrapper onClick={() => onEditRuleDeleteClicked(rule[filterType])}>
                        <SWReactIcons
                            size="sm"
                            iconName="clear"
                        />
                    </ClearFilterWrapper>
                )}
                {!activeFilterComponent && rule?.[filterType]?.length > 0 && (
                    <>
                        <RuleSelectedValuesRow
                            ruleType={ruleType}
                            filterType={filterType}
                            actualCurrentIndex={actualCurrentIndex}
                            valuesList={rule[filterType]}
                            title={i18nFilter()(
                                sortedFiltersArray.filter((filterObj) => filterObj.filterKey === filterType)[0].title,
                            )}
                            onRemoveValues={noop}
                            isFilterMode={false}
                            onRuleEdit={() => onFilterSelection(filterType)}
                            maxChipsInRow={MAX_CHIPS_IN_ROW}
                            maxChipWidth={rulesSelectedValuesMaxWidth}
                        />
                        <TrashIconWrapper
                            data-automation-trash-icon={`${ruleType}-${actualCurrentIndex}-${filterType}`}
                            onClick={() => onRuleDeleteClicked(rule[filterType])}
                        >
                            <SWReactIcons
                                size="sm"
                                iconName="delete"
                            />
                        </TrashIconWrapper>
                    </>
                )}
            </DropdownContainer>
            <ActiveFilterComponentWrapper
                isSelectedValue={!!selectedFilter}
                isActiveFilterComponent={!!activeFilterComponent}
            >
                {activeFilterComponent && (
                    <ClickOutsideWrapper onClickOutside={onClickOutsideActiveComponent}>
                        <activeFilterComponent.component {...filterComponentProps} />
                    </ClickOutsideWrapper>
                )}
            </ActiveFilterComponentWrapper>
        </>
    );
};
